/*
  Nexus Elite Solutions - 8th October 2026
  assets/charm-picker.js

  Drives <charm-picker>, and takes over Add to Cart on a product that has one so
  the necklace and every chosen charm go in as SEPARATE LINE ITEMS. That is the
  whole point of the exercise: a line item per charm is what lets Shopify count
  the stock of an A separately from a Z, and what gives a discount something to
  come off.

  HOW IT TAKES OVER THE BUTTON, AND WHY THAT WAY
  The theme's own <product-buy-buttons> listens for `submit` on the product form
  and posts the form as urlencoded, which can only ever carry ONE variant id. It
  registers that listener when theme.js upgrades the element, which happens
  before this file runs, so a second submit listener here would always be second
  in the queue and the theme would have posted already.

  So this listens for `click` on the document in the CAPTURE phase, which runs
  before anything bound to the button or the form. Cancelling the click means no
  submit event is ever fired, so the theme's handler is not raced - it simply
  never hears anything. The theme is left completely unmodified.

  Only a product that actually renders a <charm-picker> is intercepted. Every
  other product on the store keeps the theme's own behaviour untouched, because
  the handler returns immediately when it cannot find a picker.

  WHY NOT Cart.addItems()
  Because it throws away the response body: `throw new Error(res.statusText)`.
  When Shopify refuses an add it explains why in that body ("You can only add 2
  Lux Initial Charm - A to the cart"), and that sentence is the single most
  useful thing we can show. So the four public statics it would have called are
  called here directly instead, in the same order it uses them - openDrawer
  before updateAllHtml matters, because openDrawer waits for the
  theme:cart:updated that updateAllHtml fires.

  ONE REQUEST, ALL OR NOTHING
  Everything goes in a single POST to /cart/add.js. Shopify treats a multi-item
  add as atomic, so a letter that has just sold out fails the whole request and
  the cart is left exactly as it was - never a necklace sitting in the cart with
  half its charms missing.
*/

(function () {
  'use strict';

  var PICKER = 'charm-picker';

  function cartClass() {
    return window.customElements && customElements.get('cart-element');
  }

  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function addUrl() {
    var cart = (window.Shopify && Shopify.routes && Shopify.routes.cart) || '/cart';
    return cart + '/add.js';
  }

  function money(cents) {
    /* Two decimals, always. The same line is first rendered by Liquid through
       the shop's own money filter, which writes $18.00 - dropping the decimals
       here would make the figure change the moment a charm was tapped. */
    return '$' + (cents / 100).toFixed(2);
  }

  /* --------------------------------------------------------------------------
     The element
     -------------------------------------------------------------------------- */

  class CharmPicker extends HTMLElement {}

  CharmPicker.prototype.connectedCallback = function () {
    if (this.ready) return;
    this.ready = true;

    this.chosen = [];
    this.max = parseInt(this.dataset.max, 10) || 9;
    this.min = parseInt(this.dataset.min, 10) || 0;

    this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;

    this.grid = this.querySelector('.charm-picker--grid');
    this.chosenList = this.querySelector('.charm-picker--chosen');
    this.tally = this.querySelector('.charm-picker--tally');
    this.priceLine = this.querySelector('.charm-picker--price');
    this.errorBox = this.querySelector('.charm-picker--error');
    this.emptyLine = this.querySelector('.charm-picker--chosen-empty');

    if (!this.grid) return;

    this.tiles = Array.prototype.slice.call(
      this.grid.querySelectorAll('.charm-picker--charm')
    );

    var self = this;

    this.grid.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var tile = event.target.closest('.charm-picker--charm');
      if (!tile || tile.disabled) return;
      self.add(tile);
    });

    this.chosenList.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      var remove = event.target.closest('.charm-picker--chosen-remove');
      if (!remove) return;
      self.removeAt(parseInt(remove.dataset.index, 10));
    });

    this.render();
  };

  /* How many of one letter are already chosen. */
  CharmPicker.prototype.countOf = function (variantId) {
    var n = 0;
    for (var i = 0; i < this.chosen.length; i += 1) {
      if (this.chosen[i].variantId === variantId) n += 1;
    }
    return n;
  };

  CharmPicker.prototype.add = function (tile) {
    var variantId = tile.dataset.variantId;
    var stock = parseInt(tile.dataset.stock, 10) || 0;

    if (this.chosen.length >= this.max) {
      this.say('You can choose up to ' + this.max + ' charms.');
      return;
    }

    /* Per-letter cap. Shopify would refuse the add anyway, because the charm
       variants deny overselling - this just says so before the shopper has
       pressed Add to Cart. */
    if (this.countOf(variantId) >= stock) {
      this.say(
        stock === 1
          ? 'Only one ' + tile.dataset.letter + ' is in stock.'
          : 'Only ' + stock + ' of ' + tile.dataset.letter + ' are in stock.'
      );
      return;
    }

    this.chosen.push({
      variantId: variantId,
      letter: tile.dataset.letter,
      price: parseInt(tile.dataset.price, 10) || 0,
      image: tile.querySelector('.charm-picker--charm-image')
    });

    this.say('');
    this.render();
  };

  CharmPicker.prototype.removeAt = function (index) {
    if (isNaN(index) || index < 0 || index >= this.chosen.length) return;
    this.chosen.splice(index, 1);
    this.say('');
    this.render();
  };

  CharmPicker.prototype.say = function (message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message || '';
    this.errorBox.hidden = !message;
  };

  CharmPicker.prototype.render = function () {
    var self = this;

    /* The chosen row. Rebuilt wholesale rather than patched: it is at most nine
       small nodes, and rebuilding keeps the data-index values on the remove
       buttons honest after a removal from the middle. */
    var items = this.chosen.map(function (charm, index) {
      var thumb = charm.image
        ? '<img src="' + charm.image.getAttribute('src') + '" alt="" width="32" height="32">'
        : '<span class="charm-picker--chosen-letter">' + charm.letter + '</span>';

      return (
        '<li class="charm-picker--chosen-item">' +
        thumb +
        '<button type="button" class="charm-picker--chosen-remove" data-index="' +
        index +
        '" aria-label="Remove ' +
        charm.letter +
        '">&times;</button>' +
        '</li>'
      );
    });

    if (items.length) {
      this.chosenList.innerHTML = items.join('');
      this.chosenList.dataset.empty = 'false';
    } else {
      this.chosenList.innerHTML = '';
      if (this.emptyLine) this.chosenList.appendChild(this.emptyLine);
      this.chosenList.dataset.empty = 'true';
    }

    if (this.tally) {
      this.tally.textContent = this.chosen.length + ' of ' + this.max;
    }

    /*
     * The running cost, in both states - nothing chosen yet, and some chosen.
     *
     * The per-letter price is read from the charms themselves rather than
     * assumed, so if the client ever prices one letter differently the sum
     * stays right. "3 x $18.00 = $54.00" is only shown while every chosen charm
     * costs the same; otherwise the multiplication would be a lie and the plain
     * total is shown instead.
     */
    if (this.priceLine) {
      if (!this.chosen.length) {
        this.priceLine.textContent = money(this.unitPrice) + ' each';
      } else {
        var total = 0;
        var uniform = true;

        for (var p = 0; p < this.chosen.length; p += 1) {
          total += this.chosen[p].price;
          if (this.chosen[p].price !== this.chosen[0].price) uniform = false;
        }

        this.priceLine.textContent = uniform
          ? this.chosen.length + ' x ' + money(this.chosen[0].price) + ' = ' + money(total)
          : this.chosen.length + ' charms = ' + money(total);
      }
    }

    /* Mark each tile with how many of it are chosen, and grey out any letter
       that cannot be taken again - either its own stock is used up or the
       overall cap is reached. */
    var full = this.chosen.length >= this.max;

    this.tiles.forEach(function (tile) {
      var count = self.countOf(tile.dataset.variantId);
      var stock = parseInt(tile.dataset.stock, 10) || 0;

      tile.dataset.count = count;
      tile.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');

      if (!tile.hasAttribute('disabled')) {
        tile.dataset.exhausted = full || count >= stock ? 'true' : 'false';
      }
    });
  };

  /* The items array for /cart/add.js. */
  CharmPicker.prototype.lines = function (necklaceId, necklaceQty) {
    var buildId = 'gig-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 7);

    var letters = this.chosen.map(function (charm) { return charm.letter; });

    var lines = [
      {
        id: Number(necklaceId),
        quantity: necklaceQty,
        properties: {
          Charms: letters.join(', '),
          _build: buildId
        }
      }
    ];

    /* Collapse repeats into one line with a quantity, so ANNA is "A x2" rather
       than two identical rows. */
    var byVariant = {};
    this.chosen.forEach(function (charm) {
      byVariant[charm.variantId] = (byVariant[charm.variantId] || 0) + 1;
    });

    Object.keys(byVariant).forEach(function (variantId) {
      lines.push({
        id: Number(variantId),
        quantity: byVariant[variantId] * necklaceQty,
        properties: {
          _build: buildId,
          _charm: 'true'
        }
      });
    });

    return lines;
  };

  if (!window.customElements.get(PICKER)) {
    window.customElements.define(PICKER, CharmPicker);
  }

  /* --------------------------------------------------------------------------
     Taking over Add to Cart
     -------------------------------------------------------------------------- */

  function pickerFor(button) {
    var scope = button.closest('.main-product--root') || document;
    return scope.querySelector(PICKER) || document.querySelector(PICKER);
  }

  function formFor(button, picker) {
    return button.closest('form.product-form') ||
      (picker && picker.closest('form')) ||
      document.querySelector('form.product-form');
  }

  function busy(button, state) {
    button.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function submitCharmedCart(button, picker, form, checkout) {
    var Cart = cartClass();
    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');

    if (!Cart || !idInput || !idInput.value) {
      /* Nothing to take over with. Let the theme have the click back by not
         cancelling it - handled by the caller, which only cancels once this
         has agreed to run. */
      return false;
    }

    var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
    var items = picker.lines(idInput.value, qty);

    busy(button, true);
    picker.say('');

    fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items, sections: Cart.sections.join(',') })
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) {
            throw new Error(
              body.description || body.message || response.statusText
            );
          }
          return body;
        });
      })
      .then(function (cart) {
        if (checkout) {
          window.location.href = routeRoot() + 'checkout';
          return;
        }

        (cart.items || []).forEach(function (item) {
          Cart.updateBasket(item.id, item.quantity);
        });

        Cart.openDrawer(button);
        Cart.updateTotals(
          Object.values(Cart.basket).reduce(function (sum, n) { return sum + n; }, 0)
        );
        Cart.updateAllHtml(cart.sections, true);

        /* Start clean, so a second necklace is not accidentally sent with the
           first one's letters. */
        picker.chosen = [];
        picker.render();
        busy(button, false);
      })
      .catch(function (error) {
        picker.say(error.message || 'Sorry, that could not be added.');
        busy(button, false);
      });

    return true;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var button = event.target.closest(
        '.product-buy-buttons--primary, .product-buy-buttons--buy-now'
      );
      if (!button || button.disabled) return;

      var picker = pickerFor(button);
      if (!picker || !picker.ready) return;

      var form = formFor(button, picker);
      if (!form) return;

      if (picker.chosen.length < picker.min) {
        event.preventDefault();
        event.stopPropagation();
        picker.say(
          picker.min === 1
            ? 'Please choose at least one charm.'
            : 'Please choose at least ' + picker.min + ' charms.'
        );
        picker.grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var checkout = button.classList.contains('product-buy-buttons--buy-now');

      /* Cancel only once the handover has actually started, so a page where the
         cart element or the variant input is missing falls back to the theme
         instead of becoming a dead button. */
      if (submitCharmedCart(button, picker, form, checkout)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );
})();
